import { canControlVolume, useAudioMuted } from '../audio-muted.js';
import { useT } from '../i18n/LocaleProvider.js';

/**
 * 좌상단 시계 옆 **오디오 토글**. 근거와 「왜 `volume` 인가」는 `audio-muted.ts` 머리에 있다.
 *
 * ── ★ 이 앱의 첫 인라인 SVG 다 ──────────────────────────────
 *
 * 지금까지 이 앱에는 인라인 `<svg>` 도 아이콘 라이브러리도 **하나도 없었고**, 유일한
 * 「아이콘」은 사전 문자열 속 이모지였다(`ko.ts` 의 `'📺 TV에서 본 상품'`).
 *
 * **이모지(🔊/🔇)를 쓰지 않는다.** 플랫폼마다 그림이 달라 켜짐/꺼짐이 한눈에 안 갈리고,
 * 무엇보다 `verify-locale` 의 한글 훑기가 그것을 **텍스트 노드로** 잡아 「사전을 거치라」고
 * 신고한다 — 없애려면 `data-i18n` 을 붙여야 하는데 그러면 **틀까지 면제되어** 진짜 번역
 * 누락을 놓친다. 그래서 도형은 마크업으로 그리고 **문구는 `aria-label` 하나**로 둔다.
 *
 * 의존성은 0이다. 새 라이브러리를 들이지 않는다.
 *
 * ── 검증이 요구하는 둘 ───────────────────────────────────────
 *
 * - **`aria-label={t(...)}`** — 텍스트 자식이 없어 `verify-locale` 이 **속성으로만** 본다.
 *   없으면 이 버튼은 훑기의 시야 밖이다(조용한 무커버리지).
 * - **`data-testid`** — 하네스의 `clickText()` 는 `textContent` 로 찾아 **텍스트 없는 버튼을
 *   영영 못 누른다.** `clickSelector()` 를 쓰려면 이 표시가 있어야 한다.
 *
 * `<input type="checkbox">` 도 `role="switch"` 도 아니다 — 포커스 후보가
 * `button:not([disabled]), a[href]` 뿐이라 **리모컨으로 닿지 않는데 마우스로는 눌린다**
 * (절대 규칙 29). 네이티브 `<button>` 이면 **등록 없이** 포커스 링에 들어간다.
 */
export function MuteButton() {
  const t = useT();
  const [muted, setMuted] = useAudioMuted();

  /*
   * ★ **`disabled` 로 두지 않고 아예 그리지 않는다.** `disabled` 버튼도
   * `getClientRects()` 에 잡혀 자리를 차지하므로 **포커스가 빈 자리로 간다**
   * (`ControlBar.tsx` 가 기록한 그 함정). iOS 는 `volume` 을 무시하므로 여기서 빠진다.
   *
   * **판정이 둘로 늘었다** (2026-09-01) — 지금 아이폰은 `volume` **속성에는 쓴 값을
   * 남기면서 출력에는 반영하지 않아** 능력 검사만으로는 참이 되고, 그래서 **버튼이 뜨는데
   * 눌러도 소리가 그대로**였다. 근거는 `audio-muted.ts` 의 그 절.
   */
  if (!canControlVolume()) return null;

  const label = muted ? t('watch.unmute') : t('watch.mute');

  return (
    <button
      type="button"
      className={`stage__mute${muted ? ' is-on' : ''}`}
      aria-label={label}
      aria-pressed={muted}
      data-testid="mute-toggle"
      onClick={() => {
        setMuted(!muted);
      }}
    >
      {/* 스피커 몸통은 둘이 공유하고, 꺼짐일 때만 사선을 얹는다. */}
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
        <path
          d="M4 9.5h3.2L12 5.4v13.2L7.2 14.5H4z"
          fill="currentColor"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
        {muted ? (
          <path
            d="M16 9.5l5 5M21 9.5l-5 5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            fill="none"
          />
        ) : (
          <path
            d="M15.8 9a4 4 0 010 6M18.4 6.6a7.5 7.5 0 010 10.8"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            fill="none"
          />
        )}
      </svg>
    </button>
  );
}
